import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import type {
  Activity,
  CardTemplate,
  EditorStep,
  EditorCardStep,
  EditorCardGroupStep,
  EditorTransition,
  ActivityFormData,
  TransitionCondition,
} from "@/types/activity";

export function useCardTemplates() {
  return useQuery({
    queryKey: ["card-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("card_template")
        .select("*")
        .eq("enabled", true)
        .order("label");
      if (error) throw error;
      return data as CardTemplate[];
    },
  });
}

export function useActivityEditor(activityId?: string) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ActivityFormData>({
    title: "",
    summary: "",
    estimatedDurationMinutes: null,
    isDurationForced: false,
    metadata: null,
    firstStepLocalId: null,
  });
  const [steps, setSteps] = useState<EditorStep[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  // Track the DB id after first creation so subsequent saves use UPDATE
  const [createdActivityId, setCreatedActivityId] = useState<string | null>(null);
  const effectiveActivityId = activityId || createdActivityId;

  // Load existing activity
  const { isLoading: isLoadingActivity } = useQuery({
    queryKey: ["activity-editor", activityId],
    enabled: !!activityId && !loaded,
    queryFn: async () => {
      const { data: activity, error } = await supabase
        .from("activity")
        .select("*")
        .eq("id", activityId!)
        .single();
      if (error) throw error;

      // Fetch steps
      const { data: stepsData, error: stepsError } = await supabase
        .from("activity_step")
        .select("*")
        .eq("activity_id", activityId!);
      if (stepsError) throw stepsError;

      // Fetch all transitions for all steps of this activity
      const stepIds = stepsData.map((s) => s.id);
      let allTransitions: any[] = [];
      if (stepIds.length > 0) {
        const { data: transData } = await supabase
          .from("activity_step_transition")
          .select("*")
          .in("from_activity_step_id", stepIds)
          .order("order_index");
        allTransitions = transData || [];
      }

      // Build a map stepId -> localId for transition resolution
      const stepIdToLocalId = new Map<string, string>();
      const localIdByStepId = new Map<string, string>();
      
      // First pass: assign localIds
      for (const step of stepsData) {
        const localId = crypto.randomUUID();
        stepIdToLocalId.set(step.id, localId);
        localIdByStepId.set(step.id, localId);
      }

      const editorSteps: EditorStep[] = [];

      for (const step of stepsData) {
        const localId = stepIdToLocalId.get(step.id)!;
        // Build transitions for this step
        const stepTransitions: EditorTransition[] = allTransitions
          .filter((t) => t.from_activity_step_id === step.id)
          .map((t) => ({
            id: t.id,
            toStepLocalId: stepIdToLocalId.get(t.to_activity_step_id) || "",
            condition: t.condition as TransitionCondition | null,
            orderIndex: t.order_index,
          }));

        if (step.card_id) {
          const { data: card } = await supabase
            .from("card")
            .select("*, card_template(*)")
            .eq("id", step.card_id)
            .single();
          if (card) {
            const template = card.card_template as unknown as CardTemplate;
            editorSteps.push({
              type: "card",
              localId,
              existingStepId: step.id,
              existingCardId: card.id,
              templateId: card.card_template_id,
              templateCode: template?.code,
              templateLabel: template?.label,
              content: (card.content as Record<string, unknown>) || {},
              tooltips: (card as any).tooltips || undefined,
              estimatedDurationMinutes: card.estimated_duration_minutes || undefined,
              transitions: stepTransitions,
            });
          }
        } else if (step.card_group_id) {
          const { data: group } = await supabase
            .from("card_group")
            .select("*")
            .eq("id", step.card_group_id)
            .single();
          const { data: groupItems } = await supabase
            .from("card_group_item")
            .select("*, card(*, card_template(*))")
            .eq("card_group_id", step.card_group_id)
            .order("order_index");

          if (group) {
            const cards: EditorCardStep[] = (groupItems || []).map((item) => {
              const card = item.card as unknown as any;
              const template = card?.card_template as CardTemplate;
              return {
                type: "card" as const,
                localId: crypto.randomUUID(),
                existingCardId: card?.id,
                templateId: card?.card_template_id || "",
                templateCode: template?.code,
                templateLabel: template?.label,
                content: (card?.content as Record<string, unknown>) || {},
                tooltips: card?.tooltips || undefined,
                estimatedDurationMinutes: card?.estimated_duration_minutes || undefined,
                transitions: [], // group cards don't have transitions
              };
            });

            editorSteps.push({
              type: "card_group",
              localId,
              existingStepId: step.id,
              existingGroupId: group.id,
              title: group.title,
              groupType: group.group_type,
              config: (group.config as Record<string, unknown>) || undefined,
              cards,
              transitions: stepTransitions,
            });
          }
        }
      }

      // Resolve firstStepLocalId
      const firstStepLocalId = activity.first_step_id
        ? stepIdToLocalId.get(activity.first_step_id) || null
        : null;

      setFormData({
        title: activity.title,
        summary: activity.summary || "",
        estimatedDurationMinutes: activity.estimated_duration_minutes,
        isDurationForced: (activity as any).is_duration_forced ?? false,
        metadata: activity.metadata as Record<string, unknown> | null,
        firstStepLocalId,
      });

      setSteps(editorSteps);
      setIsPublished(activity.is_published);
      setLoaded(true);
      return activity;
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async ({ publish, metadataOverride, durationOverride }: { publish: boolean; metadataOverride?: Record<string, unknown> | null; durationOverride?: number | null }) => {
      if (!user) throw new Error("Non authentifié");
      if (isPublished) throw new Error("Une activité publiée ne peut pas être modifiée");
      if (!formData.title.trim()) throw new Error("Le titre est requis");

      const finalMetadata = metadataOverride !== undefined ? metadataOverride : formData.metadata;
      const finalDuration = durationOverride !== undefined ? durationOverride : formData.estimatedDurationMinutes;

      const activityPayload = {
        title: formData.title.trim(),
        summary: formData.summary.trim() || null,
        estimated_duration_minutes: finalDuration,
        is_duration_forced: formData.isDurationForced,
        metadata: finalMetadata as any,
        is_published: publish,
        updated_by: user.id,
      };

      let activityRecord: Activity;

      if (effectiveActivityId) {
        // ── UPDATE existing activity ──

        // 1. Update activity metadata (clear first_step_id temporarily)
        const { data, error } = await supabase
          .from("activity")
          .update({ ...activityPayload, first_step_id: null })
          .eq("id", effectiveActivityId)
          .select()
          .single();
        if (error) throw error;
        activityRecord = data;

        // 2. Fetch existing steps to diff
        const { data: oldSteps } = await supabase
          .from("activity_step")
          .select("id, card_id, card_group_id")
          .eq("activity_id", effectiveActivityId);

        const existingStepIds = new Set((oldSteps || []).map((s) => s.id));
        const keptStepIds = new Set<string>();

        // 3. Upsert steps
        const localIdToDbId = new Map<string, string>();
        const localCardIdToDbCardId = new Map<string, string>();
        const localGroupIdToDbGroupId = new Map<string, string>();

        for (const step of steps) {
          if (step.type === "card") {
            // Upsert card
            const cardId = step.existingCardId
              ? await updateCard(step, user.id)
              : await insertCard(step, user.id);
            // Map card localId -> DB card ID for transition condition remapping
            localCardIdToDbCardId.set(step.localId, cardId);
            if (step.existingCardId) localCardIdToDbCardId.set(step.existingCardId, cardId);

            if (step.existingStepId && existingStepIds.has(step.existingStepId)) {
              // Update existing step
              await supabase
                .from("activity_step")
                .update({ card_id: cardId, card_group_id: null })
                .eq("id", step.existingStepId);
              localIdToDbId.set(step.localId, step.existingStepId);
              keptStepIds.add(step.existingStepId);
            } else {
              // Insert new step
              const { data: newStep, error } = await supabase
                .from("activity_step")
                .insert({ activity_id: activityRecord.id, card_id: cardId })
                .select("id")
                .single();
              if (error) throw error;
              localIdToDbId.set(step.localId, newStep.id);
            }
          } else {
            // card_group step
            const groupId = step.existingGroupId
              ? await updateCardGroup(step, user.id)
              : await insertCardGroup(step, user.id);
            localGroupIdToDbGroupId.set(step.localId, groupId);
            if (step.existingGroupId) localGroupIdToDbGroupId.set(step.existingGroupId, groupId);

            // Collect existing card IDs in group to detect removed cards
            const existingGroupCardIds = new Set<string>();
            if (step.existingGroupId) {
              const { data: oldItems } = await supabase
                .from("card_group_item")
                .select("card_id")
                .eq("card_group_id", step.existingGroupId);
              (oldItems || []).forEach((i) => existingGroupCardIds.add(i.card_id));

              // Delete old group items before re-inserting
              await supabase
                .from("card_group_item")
                .delete()
                .eq("card_group_id", step.existingGroupId);
            }

            const keptGroupCardIds = new Set<string>();
            for (let j = 0; j < step.cards.length; j++) {
              const card = step.cards[j];
              const cardId = card.existingCardId
                ? await updateCard(card, user.id)
                : await insertCard(card, user.id);
              // Map card localId -> DB card ID for transition condition remapping
              localCardIdToDbCardId.set(card.localId, cardId);
              if (card.existingCardId) localCardIdToDbCardId.set(card.existingCardId, cardId);
              if (card.existingCardId) keptGroupCardIds.add(card.existingCardId);
              await supabase.from("card_group_item").insert({
                card_group_id: groupId,
                card_id: cardId,
                order_index: j,
              });
            }

            // Delete removed cards from group
            for (const oldCardId of existingGroupCardIds) {
              if (!keptGroupCardIds.has(oldCardId)) {
                await supabase.from("card").delete().eq("id", oldCardId);
              }
            }

            if (step.existingStepId && existingStepIds.has(step.existingStepId)) {
              await supabase
                .from("activity_step")
                .update({ card_group_id: groupId, card_id: null })
                .eq("id", step.existingStepId);
              localIdToDbId.set(step.localId, step.existingStepId);
              keptStepIds.add(step.existingStepId);
            } else {
              const { data: newStep, error } = await supabase
                .from("activity_step")
                .insert({ activity_id: activityRecord.id, card_group_id: groupId })
                .select("id")
                .single();
              if (error) throw error;
              localIdToDbId.set(step.localId, newStep.id);
            }
          }
        }

        // 4. Delete removed steps and their orphaned entities
        for (const oldStep of oldSteps || []) {
          if (!keptStepIds.has(oldStep.id)) {
            // Fetch group cards before deleting step
            if (oldStep.card_group_id) {
              const { data: groupItems } = await supabase
                .from("card_group_item")
                .select("card_id")
                .eq("card_group_id", oldStep.card_group_id);
              await supabase
                .from("card_group_item")
                .delete()
                .eq("card_group_id", oldStep.card_group_id);
              // Delete step first (has FK to card_group)
              await supabase.from("activity_step").delete().eq("id", oldStep.id);
              // Then delete group cards and group
              const cardIds = (groupItems || []).map((i) => i.card_id);
              if (cardIds.length > 0) {
                await supabase.from("card").delete().in("id", cardIds);
              }
              await supabase.from("card_group").delete().eq("id", oldStep.card_group_id);
            } else {
              await supabase.from("activity_step").delete().eq("id", oldStep.id);
              if (oldStep.card_id) {
                await supabase.from("card").delete().eq("id", oldStep.card_id);
              }
            }
          }
        }

        // 5. Sync transitions: delete all old, re-insert
        const allKeptStepIds = Array.from(keptStepIds);
        const allStepDbIds = Array.from(localIdToDbId.values());
        if (allStepDbIds.length > 0) {
          await supabase
            .from("activity_step_transition")
            .delete()
            .in("from_activity_step_id", allStepDbIds);
        }

        // Collect transitions grouped by fromId for normalized order_index
        const transitionsToInsert = new Map<string, Array<{ toId: string; condition: any }>>();
        for (const step of steps) {
          const fromId = localIdToDbId.get(step.localId);
          if (!fromId) continue;
          for (const t of step.transitions) {
            const toId = localIdToDbId.get(t.toStepLocalId);
            if (!toId) continue;
            const condition = remapConditionRefs(t.condition, localCardIdToDbCardId, localGroupIdToDbGroupId);
            const arr = transitionsToInsert.get(fromId) || [];
            arr.push({ toId, condition });
            transitionsToInsert.set(fromId, arr);
          }
        }
        for (const [fromId, transitions] of transitionsToInsert) {
          for (let i = 0; i < transitions.length; i++) {
            const { error: transError } = await supabase.from("activity_step_transition").insert({
              from_activity_step_id: fromId,
              to_activity_step_id: transitions[i].toId,
              condition: transitions[i].condition as any,
              order_index: i,
            });
            if (transError) throw transError;
          }
        }

        // 6. Remap nodePositions keys from localId to DB step ID for newly added steps
        const meta = (finalMetadata as Record<string, unknown>) || {};
        const oldPositions = (meta.nodePositions as Record<string, { x: number; y: number }>) || {};
        const remappedPositions: Record<string, { x: number; y: number }> = {};
        for (const step of steps) {
          const dbId = localIdToDbId.get(step.localId);
          if (!dbId) continue;
          // Try both possible keys: existingStepId (already saved) or localId (new step)
          const pos = oldPositions[step.existingStepId || ""] || oldPositions[step.localId];
          if (pos) {
            remappedPositions[dbId] = pos;
          }
        }
        const remappedMetadata = { ...meta, nodePositions: remappedPositions };

        // 7. Set first_step_id and remapped metadata
        const firstStepDbId = formData.firstStepLocalId
          ? localIdToDbId.get(formData.firstStepLocalId) || null
          : null;
        await supabase
          .from("activity")
          .update({ first_step_id: firstStepDbId, metadata: remappedMetadata as any })
          .eq("id", activityRecord.id);
      } else {
        // ── CREATE new activity ──
        const { data, error } = await supabase
          .from("activity")
          .insert([{ ...activityPayload, created_by: user.id }])
          .select()
          .single();
        if (error) throw error;
        activityRecord = data;
        setCreatedActivityId(data.id);
        const localIdToDbId = new Map<string, string>();
        const localCardIdToDbCardId = new Map<string, string>();
        const localGroupIdToDbGroupId = new Map<string, string>();

        for (const step of steps) {
          if (step.type === "card") {
            const cardId = await insertCard(step, user.id);
            localCardIdToDbCardId.set(step.localId, cardId);
            const { data: newStep, error } = await supabase
              .from("activity_step")
              .insert({ activity_id: activityRecord.id, card_id: cardId })
              .select("id")
              .single();
            if (error) throw error;
            localIdToDbId.set(step.localId, newStep.id);
          } else {
            const groupId = await insertCardGroup(step, user.id);
            localGroupIdToDbGroupId.set(step.localId, groupId);
            for (let j = 0; j < step.cards.length; j++) {
              const cardId = await insertCard(step.cards[j], user.id);
              localCardIdToDbCardId.set(step.cards[j].localId, cardId);
              await supabase.from("card_group_item").insert({
                card_group_id: groupId,
                card_id: cardId,
                order_index: j,
              });
            }
            const { data: newStep, error } = await supabase
              .from("activity_step")
              .insert({ activity_id: activityRecord.id, card_group_id: groupId })
              .select("id")
              .single();
            if (error) throw error;
            localIdToDbId.set(step.localId, newStep.id);
          }
        }

        // Collect transitions grouped by fromId for normalized order_index
        const transitionsToInsertCreate = new Map<string, Array<{ toId: string; condition: any }>>();
        for (const step of steps) {
          const fromId = localIdToDbId.get(step.localId);
          if (!fromId) continue;
          for (const t of step.transitions) {
            const toId = localIdToDbId.get(t.toStepLocalId);
            if (!toId) continue;
            const condition = remapConditionRefs(t.condition, localCardIdToDbCardId, localGroupIdToDbGroupId);
            const arr = transitionsToInsertCreate.get(fromId) || [];
            arr.push({ toId, condition });
            transitionsToInsertCreate.set(fromId, arr);
          }
        }
        for (const [fromId, transitions] of transitionsToInsertCreate) {
          for (let i = 0; i < transitions.length; i++) {
            const { error: transError } = await supabase.from("activity_step_transition").insert({
              from_activity_step_id: fromId,
              to_activity_step_id: transitions[i].toId,
              condition: transitions[i].condition as any,
              order_index: i,
            });
            if (transError) throw transError;
          }
        }

        const firstStepDbId = formData.firstStepLocalId
          ? localIdToDbId.get(formData.firstStepLocalId) || null
          : null;

        // Remap nodePositions keys from localId to DB step ID so positions survive reload
        const meta = (finalMetadata as Record<string, unknown>) || {};
        const oldPositions = (meta.nodePositions as Record<string, { x: number; y: number }>) || {};
        const remappedPositions: Record<string, { x: number; y: number }> = {};
        for (const step of steps) {
          // positionKey for new steps = localId (no existingStepId yet)
          const oldKey = step.localId;
          const dbId = localIdToDbId.get(step.localId);
          const pos = oldPositions[oldKey];
          if (pos && dbId) {
            remappedPositions[dbId] = pos;
          }
        }
        const remappedMetadata = { ...meta, nodePositions: remappedPositions };

        if (firstStepDbId || Object.keys(remappedPositions).length > 0) {
          await supabase
            .from("activity")
            .update({
              first_step_id: firstStepDbId,
              metadata: remappedMetadata as any,
            })
            .eq("id", activityRecord.id);
        }

        // Update local steps with their DB IDs so subsequent saves use UPDATE path correctly
        setSteps((prev) =>
          prev.map((s) => {
            const updated: any = {
              ...s,
              existingStepId: localIdToDbId.get(s.localId) || s.existingStepId,
            };
            if (s.type === "card") {
              updated.existingCardId = localCardIdToDbCardId.get(s.localId) || s.existingCardId;
            } else if (s.type === "card_group") {
              updated.cards = (s as EditorCardGroupStep).cards.map((c) => ({
                ...c,
                existingCardId: localCardIdToDbCardId.get(c.localId) || c.existingCardId,
              }));
            }
            return updated;
          })
        );
      }

      return activityRecord;
    },
    onSuccess: (_, { publish }) => {
      toast.success(publish ? "Activité publiée" : "Activité sauvegardée");
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["activity-editor", effectiveActivityId] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return {
    formData,
    setFormData,
    steps,
    setSteps,
    isLoading: isLoadingActivity,
    isSaving: saveMutation.isPending,
    isPublished,
    save: (publish: boolean, metadataOverride?: Record<string, unknown> | null, durationOverride?: number | null) =>
      saveMutation.mutateAsync({ publish, metadataOverride, durationOverride }),
  };
}

async function insertCard(card: EditorCardStep, userId: string): Promise<string> {
  const { data, error } = await supabase
    .from("card")
    .insert({
      card_template_id: card.templateId,
      content: card.content as any,
      tooltips: card.tooltips || null,
      estimated_duration_minutes: card.estimatedDurationMinutes || null,
      created_by: userId,
      updated_by: userId,
    } as any)
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

async function updateCard(card: EditorCardStep, userId: string): Promise<string> {
  const { error } = await supabase
    .from("card")
    .update({
      card_template_id: card.templateId,
      content: card.content as any,
      tooltips: card.tooltips || null,
      estimated_duration_minutes: card.estimatedDurationMinutes || null,
      updated_by: userId,
    } as any)
    .eq("id", card.existingCardId!);
  if (error) throw error;
  return card.existingCardId!;
}

async function insertCardGroup(
  group: EditorCardGroupStep,
  userId: string
): Promise<string> {
  const { data, error } = await supabase
    .from("card_group")
    .insert({
      title: group.title,
      group_type: group.groupType,
      config: (group.config as any) || null,
      created_by: userId,
      updated_by: userId,
    })
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

async function updateCardGroup(
  group: EditorCardGroupStep,
  userId: string
): Promise<string> {
  const { error } = await supabase
    .from("card_group")
    .update({
      title: group.title,
      group_type: group.groupType,
      config: (group.config as any) || null,
      updated_by: userId,
    })
    .eq("id", group.existingGroupId!);
  if (error) throw error;
  return group.existingGroupId!;
}

/** Recursively remap card_id and card_group_id references in transition conditions */
function remapConditionRefs(
  condition: TransitionCondition | null,
  cardMap: Map<string, string>,
  groupMap: Map<string, string>,
): TransitionCondition | null {
  if (!condition) return null;
  if (condition.type === "compound") {
    return {
      ...condition,
      conditions: condition.conditions.map((sub) => remapConditionRefs(sub, cardMap, groupMap)!).filter(Boolean) as any,
    };
  }
  const c = { ...condition } as any;
  if (c.card_id) c.card_id = cardMap.get(c.card_id) || c.card_id;
  if (c.card_group_id) c.card_group_id = groupMap.get(c.card_group_id) || c.card_group_id;
  if (c.card_id_a) c.card_id_a = cardMap.get(c.card_id_a) || c.card_id_a;
  if (c.card_id_b) c.card_id_b = cardMap.get(c.card_id_b) || c.card_id_b;
  if (c.card_group_id_a) c.card_group_id_a = groupMap.get(c.card_group_id_a) || c.card_group_id_a;
  if (c.card_group_id_b) c.card_group_id_b = groupMap.get(c.card_group_id_b) || c.card_group_id_b;
  return c;
}

